import { authApi, siteApi } from "@api";
import { DEFAULT_ERROR_MESSAGE } from "@constants/misc";
import { ValidatorSchema } from "@constants/validators";
import { zodResolver } from "@hookform/resolvers/zod";
import useContactLinks from "@hooks/useContactLinks";
import { AppDispatch } from "@stores";
import authSlice from "@stores/auth/slices.ts";
import {
  APIErrorCodes,
  Button,
  CreateFundingProfileRequest,
  FundingProfileType,
  ProfileResponse,
  PropsWithClassName,
  TextField,
  cn,
  useHandleApiResponse,
} from "kz-ui-sdk";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { z as zod } from "zod";
import BankSelect from "../BankSelect";
import RHFController from "../RHFController";

interface UpdateBankAccountFormProps extends PropsWithClassName {
  onUpdateSuccess?: (response?: ProfileResponse) => void;
  mode?: "update" | "add";
}

const schema = zod.object({
  accountNo: ValidatorSchema.payment.bank.accountNo,
  firstname: ValidatorSchema.firstName,
  lastname: ValidatorSchema.lastName,
  bankKey: ValidatorSchema.payment.bank.bankKey,
});

type Values = zod.infer<typeof schema>;

const defaultValue = { accountNo: "", firstname: "", lastname: "", bankKey: "" } satisfies Values;

const FormUpdateBankAccount = ({ onUpdateSuccess, className, mode }: UpdateBankAccountFormProps) => {
  const { handleApiResponse } = useHandleApiResponse({
    toast,
  });
  const { t } = useTranslation();
  const [addBankAcc, { isLoading: isAddingBankAcc }] = siteApi.useCreateFundingProfileMutation();
  const [updatePersonalInfo, { isLoading: isUpdatingPersonalInfo }] = siteApi.useUpdatePersonalInfoMutation();

  const dispatch = useDispatch<AppDispatch>();

  const { openContact } = useContactLinks();

  const handleOpenContactLink = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      openContact();
    },
    [openContact],
  );

  const {
    handleSubmit,
    control,
    setError,
    setValue,
    formState: { errors },
  } = useForm<Values>({
    mode: "onChange",
    defaultValues: defaultValue,
    resolver: zodResolver(schema),
  });

  const onSubmit = useCallback(
    async (v: Values) => {
      const requestBankInfo: CreateFundingProfileRequest = {
        key: v.bankKey,
        accNo: v.accountNo.trim(),
        type: FundingProfileType.Bank,
        isPrimary: true,
      };

      const result = await addBankAcc(requestBankInfo);
      handleApiResponse(result, {
        successMessage: t("Updated bank account successfully!"),
        toastError: false,
        onSuccess: async () => {
          // update profile
          await updatePersonalInfo({
            firstname: v.firstname?.trim(),
            lastname: v.lastname?.trim(),
          });

          // refresh self
          const result = await dispatch(
            authApi.endpoints.getProfile.initiate(undefined, {
              forceRefetch: true,
            }),
          );
          if (result.data) {
            dispatch(authSlice.actions.updateProfile(result.data));
          }

          onUpdateSuccess?.(result.data);
        },
        onError: (error) => {
          const errorMessage = error.data?.error?.message;
          switch (errorMessage) {
            case APIErrorCodes.FundingProfile.LinkedToAnotherMember:
              setError("accountNo", { message: "account number is not valid" });
              break;
            default:
              toast.error(t(errorMessage ?? DEFAULT_ERROR_MESSAGE));
              return;
          }
        },
      });
    },
    [addBankAcc, dispatch, handleApiResponse, onUpdateSuccess, t, updatePersonalInfo, setError],
  );

  const isLoading = isAddingBankAcc || isUpdatingPersonalInfo;

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      <RHFController
        control={control}
        name="bankKey"
      >
        <BankSelect disabled={isLoading} />
      </RHFController>

      <label className="flex gap-2">
        <RHFController
          control={control}
          name="firstname"
          onBlur={(v) => {
            setValue("firstname", v.trim());
          }}
        >
          <TextField
            classes={{
              "input&": "w-full truncate",
            }}
            placeholder={t("First name")}
            disabled={isLoading}
            size={"lg"}
          />
        </RHFController>
        <RHFController
          control={control}
          name="lastname"
          onBlur={(v) => {
            setValue("lastname", v.trim());
          }}
        >
          <TextField
            classes={{
              "input&": "w-full truncate",
            }}
            placeholder={t("Last name")}
            disabled={isLoading}
            size={"lg"}
          />
        </RHFController>
      </label>
      <label className="flex flex-col gap-2">
        <RHFController
          control={control}
          name="accountNo"
          onBlur={(v) => {
            setValue("accountNo", v.trim());
          }}
        >
          <TextField
            classes={{
              "input&": "w-full truncate",
            }}
            helperTextClasses={{
              className: "h-0 overflow-hidden",
            }}
            placeholder={t("Account number")}
            disabled={isLoading}
            size={"lg"}
          />
        </RHFController>
        {errors.accountNo && (
          <div
            className={cn("-mt-1.5 max-h-0 text-right text-xs leading-4 text-error-base transition-[max-height]", {
              "max-h-5": !!errors.accountNo.message,
            })}
          >
            {errors.accountNo.message === "account number is not valid" && (
              <span className="h-5 text-error-base">
                {t("account number is not valid")}{" "}
                <button
                  onClick={handleOpenContactLink}
                  className="underline"
                >
                  {t("contact cs")}
                </button>
              </span>
            )}
            {errors.accountNo.message !== "account number is not valid" && (
              <span className="h-5 text-error-base">{t(errors.accountNo.message || "")}</span>
            )}
          </div>
        )}
      </label>
      <Button
        onClick={handleSubmit(onSubmit)}
        loading={isLoading}
        size={"lg"}
        className="mt-3"
      >
        {mode === "update" ? t("Update") : t("finish & login")}
      </Button>
    </div>
  );
};

export default FormUpdateBankAccount;
